import React from "react";
import { Column } from "@/components/Datatable/types";
import { ChapterActivity } from "@/api/fetchChapterActivities/types";
import ChapterActivityActionMenu from "@/features/myChapterActivity/components/ChapterActivityActionMenu/ChapterActivityActionMenu";

/**
 * Helper to format date for display
 */
function formatDate(dateString?: string | null): string {
  if (!dateString) return "--";

  // For simple date strings, we can just format them directly
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // If invalid, return original

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString; // If parsing fails, return original
  }
}

/**
 * Helper to truncate text with ellipsis
 */
function truncateText(text?: string | null, maxLength: number = 50): string {
  if (!text) return "--";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

/**
 * Props for the columns generator
 */
interface CreateChapterActivitiesColumnsProps {
  onViewDetails: (activity: ChapterActivity) => void;
}

/**
 * Generates columns for the chapter activities table
 */
export function createChapterActivitiesColumns(
  props: CreateChapterActivitiesColumnsProps,
): Column<ChapterActivity>[] {
  return [
    {
      header: "Activity Name",
      accessorKey: "chapterActivityName",
      cell: ({ row }) => truncateText(row.original.chapterActivityName, 30),
    },
    {
      header: "Activity Type",
      accessorKey: "activityType",
      cell: ({ row }) => row.original.activityType || "--",
    },
    {
      header: "Date",
      accessorKey: "chapterActivityDate",
      cell: ({ row }) => formatDate(row.original.chapterActivityDate),
    },
    {
      header: "Advisor Present",
      accessorKey: "advisorPresent",
      cell: ({ row }) =>
        row.original.advisorPresent === "true" ? "Yes" : "No",
    },
    {
      header: "NSCS Members",
      accessorKey: "numOfNSCSMembers",
      cell: ({ row }) => row.original.numOfNSCSMembers.toString(),
    },
    {
      header: "Non-NSCS Members",
      accessorKey: "numOfNonNSCSMembers",
      cell: ({ row }) => row.original.numOfNonNSCSMembers.toString(),
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => {
        const activity = row.original;
        return (
          <ChapterActivityActionMenu
            activity={activity}
            onViewDetails={props.onViewDetails}
          />
        );
      },
    },
  ];
}
